import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import styled from 'styled-components'

import { Span } from '@/@bothrs-ui/web/components/typography/Body'

import type { ComponentPropsWithoutRef } from 'react'

/* ------------------------------------------------------------------------------------------------- *
 * MedicationPhotoBadge
 * ------------------------------------------------------------------------------------------------- */

type MedicationPhotoBadgeRootProps = ComponentPropsWithoutRef<'div'>

const MedicationPhotoBadgeRoot = ({ children, ...props }: MedicationPhotoBadgeRootProps) => (
  <StRoot {...props}>{children}</StRoot>
)

/* ------------------------------------------------------------------------------------------------- *
 * MedicationPhotoBadge.Content
 * ------------------------------------------------------------------------------------------------- */

type MedicationPhotoBadgeContentProps = ComponentPropsWithoutRef<'span'>

/** Centres the icon and label in the space next to the photo. */
const MedicationPhotoBadgeContent = ({ children, ...props }: MedicationPhotoBadgeContentProps) => (
  <StContent {...props}>{children}</StContent>
)

/* ------------------------------------------------------------------------------------------------- *
 * MedicationPhotoBadge.Icon
 * ------------------------------------------------------------------------------------------------- */

type MedicationPhotoBadgeIconProps = ComponentPropsWithoutRef<typeof FontAwesomeIcon>

const MedicationPhotoBadgeIcon = (props: MedicationPhotoBadgeIconProps) => <StIcon {...props} />

/* ------------------------------------------------------------------------------------------------- *
 * MedicationPhotoBadge.Label
 * ------------------------------------------------------------------------------------------------- */

type MedicationPhotoBadgeLabelProps = ComponentPropsWithoutRef<typeof Span>

const MedicationPhotoBadgeLabel = ({ children, ...props }: MedicationPhotoBadgeLabelProps) => (
  <StLabel type='BodySmallBold' {...props}>
    {children}
  </StLabel>
)

/* ------------------------------------------------------------------------------------------------- *
 * MedicationPhotoBadge.Text
 * ------------------------------------------------------------------------------------------------- */

type MedicationPhotoBadgeTextProps = ComponentPropsWithoutRef<typeof Span>

/** Plain text next to the photo, such as that the file was processed. */
const MedicationPhotoBadgeText = ({ children, ...props }: MedicationPhotoBadgeTextProps) => (
  <StText type='BodySmallRegular' {...props}>
    {children}
  </StText>
)

/* ------------------------------------------------------------------------------------------------- *
 * Compound export
 * ------------------------------------------------------------------------------------------------- */

const MedicationPhotoBadge = Object.assign(MedicationPhotoBadgeRoot, {
  Content: MedicationPhotoBadgeContent,
  Icon: MedicationPhotoBadgeIcon,
  Label: MedicationPhotoBadgeLabel,
  Text: MedicationPhotoBadgeText,
})

export { MedicationPhotoBadge }

/* ------------------------------------------------------------------------------------------------- *
 * Styled
 * ------------------------------------------------------------------------------------------------- */

const StRoot = styled.div`
  display: flex;
  align-items: center;

  ${({ theme }) => `
    gap: ${theme.UI.SpacingPx.Space4};
    padding: ${theme.UI.SpacingPx.Space1} ${theme.UI.SpacingPx.Space2};
    border: 1px solid ${theme.Colors.Neutrals.Light};
    border-radius: ${theme.UI.BorderRadius.Small};
  `}
`

const StContent = styled.span`
  display: flex;
  flex: 1;
  justify-content: center;
  align-items: center;
  gap: ${({ theme }) => theme.UI.SpacingPx.Space2};
`

const StIcon = styled(FontAwesomeIcon)`
  width: 16px;
  height: 16px;
  color: ${({ theme }) => theme.Colors.Theme.Primary};
`

const StLabel = styled(Span)`
  color: ${({ theme }) => theme.Colors.Theme.Primary};
`

const StText = styled(Span)`
  color: ${({ theme }) => theme.Colors.Neutrals.Darker};
`
